import { useState, useMemo } from "react"
import { Box, Table, TableHead, TableRow, TableCell, TableBody, Typography } from "@mui/material"
import { capitalize, groupBy } from "lodash"
import { FACTOR_RULE_TYPES } from "components/search-factor/constants"
import { FACTOR_CURRENT_COLUMN_LABELS } from "../constants"
import type { FactorRuleDisplay, FactorRuleDisplayType, FactorRuleField } from "../types"
import { FactorRulesGroup } from "./group"

import "./style.scss"

export interface FactorRulesTableProps {
  rules: FactorRuleDisplay[]
  handleUpdateRule: (rule: FactorRuleDisplay, field: FactorRuleField, value: string | undefined, ruleDisplayType: FactorRuleDisplayType) => void
  handleRemoveRule: (rule: FactorRuleDisplay, ruleDisplayType: FactorRuleDisplayType) => void
  handleToggleActiveValue?: (rule: FactorRuleDisplay, ruleDisplayType: FactorRuleDisplayType) => void
  ruleDisplayType: FactorRuleDisplayType
  hasBase?: boolean
}

export function getTableHeaders(ruleDisplayType: FactorRuleDisplayType, hasBase: boolean) {
  const headers = [
    { key: "factor", label: "Factor" },
    { key: "current", label: <>Current<br />{FACTOR_CURRENT_COLUMN_LABELS[ruleDisplayType]}</> },
    ...(hasBase ? [{ key: "active", label: <>Active<br />{FACTOR_CURRENT_COLUMN_LABELS[ruleDisplayType]}</> }] : []),
    { key: "min", label: "Min" },
    { key: "max", label: "Max" },
    { key: "actions", label: "" },
  ]

  return headers
}

function getGroupKey(rule: FactorRuleDisplay): string {
  if (rule.type === FACTOR_RULE_TYPES.CLASSIFICATION) {
    return rule.groupById || "Unknown Classification"
  } else if (rule.type === FACTOR_RULE_TYPES.FACTOR) {
    return "Risk Factor"
  } else if (rule.contentSetName) {
    return rule.contentSetName
  } else {
    return capitalize(rule.category) || "Other"
  }
}

export function FactorRulesTable({
  rules,
  handleUpdateRule,
  handleRemoveRule,
  handleToggleActiveValue,
  ruleDisplayType,
  hasBase = false,
}: FactorRulesTableProps) {
  const capitalizedRuleDisplayType = capitalize(ruleDisplayType)

  // Group rules by their dataset type
  const groupedRules = groupBy(rules, getGroupKey)
  const sortedGroupKeys = Object.keys(groupedRules).sort()

  // Track which groups user manually collapsed and their rule count at collapse time
  const [collapsedGroupsWithCounts, setCollapsedGroupsWithCounts] = useState<Record<string, number>>({})

  // Derive which groups are currently collapsed
  // A group is collapsed only if user collapsed it AND no new rules were added since then
  const collapsedGroups = useMemo(
    () => new Set(
      Object.entries(collapsedGroupsWithCounts)
        .filter(([groupKey, collapsedAtCount]) =>
          (groupedRules[groupKey]?.length || 0) === collapsedAtCount,
        )
        .map(([groupKey]) => groupKey),
    ),
    [collapsedGroupsWithCounts, groupedRules],
  )

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroupsWithCounts((prev) => {
      const isCurrentlyCollapsed = groupKey in prev
      const currentCount = groupedRules[groupKey]?.length || 0

      if (isCurrentlyCollapsed) {
        // Expanding - remove from collapsed tracking
        const next = Object.fromEntries(
          Object.entries(prev).filter(([key]) => key !== groupKey),
        )
        return next
      } else {
        // Collapsing - track with current count
        return {
          ...prev,
          [groupKey]: currentCount,
        }
      }
    })
  }

  if (rules.length === 0) return null

  return <Box className="factor-rules-table-container">
    <Typography variant="subtitle2" className="factor-rules-table-title">
      {capitalizedRuleDisplayType}
    </Typography>
    <Table className="factor-rules-table" size="small">
      <TableHead className="table-head">
        <TableRow className="table-row">
          {getTableHeaders(ruleDisplayType, hasBase).map((header) => (
            <TableCell className={`table-cell column-${header.key}`} key={header.key}>
              {header.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedGroupKeys.map((groupKey) => (
          <FactorRulesGroup
            key={groupKey}
            groupKey={groupKey}
            groupRules={groupedRules[groupKey]}
            allRules={rules}
            isExpanded={!collapsedGroups.has(groupKey)}
            ruleDisplayType={ruleDisplayType}
            hasBase={hasBase}
            onToggle={() => toggleGroup(groupKey)}
            handleUpdateRule={handleUpdateRule}
            handleRemoveRule={handleRemoveRule}
            handleToggleActiveValue={handleToggleActiveValue}
          />
        ))}
      </TableBody>
    </Table>
  </Box>
}
